/**
 * Streaming-optimized code parser for real-time AI response processing
 * Efficiently handles partial content during streaming scenarios
 */

import { type CodeBlock, type ParsedContent } from './code-parser';

export interface StreamingParserState {
  buffer: string;
  lastParsedIndex: number;
  activeCodeBlocks: Map<string, Partial<CodeBlock>>;
  completedBlocks: CodeBlock[];
  parseVersion: number;
}

export interface StreamingParseResult extends ParsedContent {
  newBlocks: CodeBlock[];
  updatedBlocks: CodeBlock[];
  parserState: StreamingParserState;
}

const CODE_START_DELIMITER = '<<<GIATEC_CODE_START';
const CODE_END_DELIMITER = '<<<GIATEC_CODE_END>>>';
const START_DELIMITER_REGEX = /<<<GIATEC_CODE_START(?::(\w+))?>>>/g;
const END_DELIMITER_REGEX = /<<<GIATEC_CODE_END>>>/g;

/**
 * Create initial streaming parser state
 */
export function createStreamingParserState(): StreamingParserState {
  return {
    buffer: '',
    lastParsedIndex: 0,
    activeCodeBlocks: new Map(),
    completedBlocks: [],
    parseVersion: 0
  };
}

/**
 * Process streaming content incrementally
 * Only parses new content since last update for efficiency
 */
export function parseStreamingContent(
  newContent: string,
  previousState?: StreamingParserState
): StreamingParseResult {
  const state = previousState || createStreamingParserState();
  
  // Append new content to buffer
  state.buffer += newContent;
  state.parseVersion++;

  // Only parse the new portion plus some overlap for safety
  const parseStart = Math.max(0, state.lastParsedIndex - 100); // 100 char overlap
  const contentToParse = state.buffer.slice(parseStart);
  const newBlocks: CodeBlock[] = [];
  const updatedBlocks: CodeBlock[] = [];

  // Find all delimiter positions in the content to parse
  const startPositions = findDelimiterPositions(contentToParse, START_DELIMITER_REGEX);
  const endPositions = findDelimiterPositions(contentToParse, END_DELIMITER_REGEX);

  // Process start delimiters
  for (const startPos of startPositions) {
    if (startPos.index + parseStart < state.lastParsedIndex) continue; // Skip already processed

    const blockId = `streaming-${state.parseVersion}-${startPos.index}`;
    const language = startPos.match[1] || 'text';
    
    const partialBlock: Partial<CodeBlock> = {
      id: blockId,
      language,
      content: '',
      startIndex: startPos.index + parseStart,
      isComplete: false
    };

    state.activeCodeBlocks.set(blockId, partialBlock);
  }

  // Process end delimiters and complete blocks
  for (const endPos of endPositions) {
    if (endPos.index + parseStart < state.lastParsedIndex) continue; // Skip already processed

    // Find the matching start delimiter for this end
    const matchingStartId = findMatchingStart(endPos.index + parseStart, state.activeCodeBlocks);
    
    if (matchingStartId) {
      const partialBlock = state.activeCodeBlocks.get(matchingStartId);
      if (partialBlock && partialBlock.startIndex !== undefined) {
        // Calculate content between delimiters
        const contentStart = findContentStart(state.buffer, partialBlock.startIndex, partialBlock.language || 'text');
        const contentEnd = endPos.index + parseStart;
        const codeContent = state.buffer.slice(contentStart, contentEnd);

        const completedBlock: CodeBlock = {
          id: partialBlock.id!,
          language: partialBlock.language!,
          content: codeContent.trim(),
          startIndex: partialBlock.startIndex,
          endIndex: contentEnd + CODE_END_DELIMITER.length,
          isComplete: true
        };

        state.completedBlocks.push(completedBlock);
        newBlocks.push(completedBlock);
        state.activeCodeBlocks.delete(matchingStartId);
      }
    }
  }

  // Update incomplete blocks with current content
  for (const [blockId, partialBlock] of state.activeCodeBlocks) {
    if (partialBlock.startIndex !== undefined) {
      const contentStart = findContentStart(state.buffer, partialBlock.startIndex, partialBlock.language || 'text');
      const currentContent = state.buffer.slice(contentStart);
      
      // Create updated incomplete block
      const updatedBlock: CodeBlock = {
        id: partialBlock.id!,
        language: partialBlock.language!,
        content: currentContent.trim(),
        startIndex: partialBlock.startIndex,
        endIndex: state.buffer.length,
        isComplete: false
      };

      updatedBlocks.push(updatedBlock);
    }
  }

  // Generate processed content by removing delimiters
  const processedContent = generateProcessedContent(state.buffer, [
    ...state.completedBlocks,
    ...Array.from(state.activeCodeBlocks.values()).map(pb => ({
      id: pb.id!,
      language: pb.language!,
      content: '',
      startIndex: pb.startIndex!,
      endIndex: state.buffer.length,
      isComplete: false
    } as CodeBlock))
  ]);

  // Update last parsed index
  state.lastParsedIndex = state.buffer.length;

  const allCodeBlocks = [
    ...state.completedBlocks,
    ...updatedBlocks
  ].sort((a, b) => a.startIndex - b.startIndex);

  return {
    processedContent,
    codeBlocks: allCodeBlocks,
    hasIncompleteBlocks: state.activeCodeBlocks.size > 0,
    newBlocks,
    updatedBlocks,
    parserState: state
  };
}

/**
 * Find all positions of a delimiter pattern
 */
function findDelimiterPositions(content: string, regex: RegExp): Array<{index: number, match: RegExpExecArray}> {
  const positions: Array<{index: number, match: RegExpExecArray}> = [];
  let match;
  
  // Reset regex state
  regex.lastIndex = 0;
  
  while ((match = regex.exec(content)) !== null) {
    positions.push({ index: match.index, match });
  }
  
  return positions;
}

/**
 * Find the matching start delimiter for an end position
 */
function findMatchingStart(endIndex: number, activeBlocks: Map<string, Partial<CodeBlock>>): string | null {
  let closestStart: { id: string; index: number } | null = null;
  
  for (const [blockId, block] of activeBlocks) {
    if (block.startIndex !== undefined && block.startIndex < endIndex) {
      if (!closestStart || block.startIndex > closestStart.index) {
        closestStart = { id: blockId, index: block.startIndex };
      }
    }
  }
  
  return closestStart?.id || null;
}

/**
 * Find where the actual code content starts after the delimiter
 */
function findContentStart(buffer: string, startIndex: number, language: string): number {
  const delimiterEnd = buffer.indexOf('>>>', startIndex);
  return delimiterEnd !== -1 ? delimiterEnd + 3 : startIndex;
}

/**
 * Generate processed content with delimiters replaced by markdown
 */
function generateProcessedContent(buffer: string, codeBlocks: CodeBlock[]): string {
  let processed = buffer;
  
  // Sort blocks by start index in reverse order to avoid index shifting
  const sortedBlocks = [...codeBlocks].sort((a, b) => b.startIndex - a.startIndex);
  
  for (const block of sortedBlocks) {
    if (block.isComplete) {
      // Replace custom delimiter with markdown
      const beforeBlock = processed.slice(0, block.startIndex);
      const afterBlock = processed.slice(block.endIndex);
      const markdownBlock = `\`\`\`${block.language}\n${block.content}\n\`\`\``;
      
      processed = beforeBlock + markdownBlock + afterBlock;
    } else {
      // Remove incomplete start delimiter but keep content
      const delimiterStart = processed.lastIndexOf(CODE_START_DELIMITER, block.startIndex + 50);
      if (delimiterStart !== -1) {
        const delimiterEnd = processed.indexOf('>>>', delimiterStart);
        if (delimiterEnd !== -1) {
          processed = processed.slice(0, delimiterStart) + processed.slice(delimiterEnd + 3);
        }
      }
    }
  }
  
  return processed;
}

/**
 * Reset streaming parser state (useful for new conversations)
 */
export function resetStreamingParser(): StreamingParserState {
  return createStreamingParserState();
}

/**
 * Get streaming parser statistics for debugging
 */
export function getParserStats(state: StreamingParserState): {
  bufferSize: number;
  activeBlocks: number;
  completedBlocks: number;
  parseVersion: number;
  efficiency: string;
} {
  const efficiency = state.lastParsedIndex > 0 
    ? ((state.lastParsedIndex / state.buffer.length) * 100).toFixed(1) + '%'
    : '0%';

  return {
    bufferSize: state.buffer.length,
    activeBlocks: state.activeCodeBlocks.size,
    completedBlocks: state.completedBlocks.length,
    parseVersion: state.parseVersion,
    efficiency
  };
}

/**
 * Optimized content processing for large streams
 * Trims old content to prevent memory bloat
 */
export function optimizeStreamingBuffer(
  state: StreamingParserState,
  maxBufferSize: number = 50000
): StreamingParserState {
  if (state.buffer.length <= maxBufferSize) {
    return state;
  }

  // Keep the last maxBufferSize characters
  const trimStart = state.buffer.length - maxBufferSize;
  const newBuffer = state.buffer.slice(trimStart);
  
  // Adjust indices for remaining blocks
  const adjustedActiveBlocks = new Map<string, Partial<CodeBlock>>();
  for (const [id, block] of state.activeCodeBlocks) {
    if (block.startIndex !== undefined && block.startIndex >= trimStart) {
      adjustedActiveBlocks.set(id, {
        ...block,
        startIndex: block.startIndex - trimStart
      });
    }
  }

  const adjustedCompletedBlocks = state.completedBlocks
    .filter(block => block.startIndex >= trimStart)
    .map(block => ({
      ...block,
      startIndex: block.startIndex - trimStart,
      endIndex: block.endIndex - trimStart
    }));

  return {
    ...state,
    buffer: newBuffer,
    lastParsedIndex: Math.max(0, state.lastParsedIndex - trimStart),
    activeCodeBlocks: adjustedActiveBlocks,
    completedBlocks: adjustedCompletedBlocks
  };
}